import { ref, onUnmounted, triggerRef, unref, computed } from 'vue';
import { v4 as uuidv4 } from 'uuid';

const listStores = new Map();

export function useList (storeId = 'default', options) {
    if (!listStores.has(storeId)) {
        listStores.set(storeId, {
            key: 'id',
            items: ref({}),
            selectedItemId: ref(null),
            subscribers: new Set(),
            orderBy: ref({
                field: null,
                dir: 'asc',
            }),
        });
    }

    const store = listStores.get(storeId);
    const subscriberUuid = uuidv4();
    store.subscribers.add(subscriberUuid);

    const addItem = (item, key) => {
        const itemKey = item[key || store.key];
        store.items.value[itemKey] = item;
    };

    const addItems = (newItems, key) => {
        newItems.forEach((item) => {
            const itemKey = item[key || store.key];
            store.items.value[itemKey] = item;
        });
    };

    const updateItem = (id, key, value) => {
        if (typeof key === 'string') {
            store.items.value[id] = {
                ...store.items.value[id],
                [key]: value,
            };
        } else {
            store.items.value[id] = {
                ...store.items.value[id],
                ...key,
            };
        }
    };

    const removeItem = (id) => {
        delete store.items.value[id];
        triggerRef(store.items);

        if (unref(store.selectedItemId) && unref(store.selectedItemId).id === id) {
            store.selectedItemId.value = null;
        }
    };

    const removeItems = (ids) => {
        ids.forEach((id) => {
            delete store.items.value[id];
        });
        triggerRef(store.items);

        if (unref(store.selectedItemId) && ids.includes(unref(store.selectedItemId).id)) {
            store.selectedItemId.value = null;
        }
    };

    const clearList = () => {
        store.items.value = {};
        store.selectedItemId.value = null;
        triggerRef(store.items);
    };

    const selectItem = (id) => {
        store.selectedItemId.value = id || null;
    };

    const setOrderBy = (orderBy, dir = 'asc') => {
        store.orderBy.value = { field: orderBy, dir };
    };

    const cleanup = () => {
        if (subscriberUuid) {
            store.subscribers.delete(subscriberUuid);

            if (store.subscribers.size === 0) {
                listStores.delete(storeId);
            }
        }
    };

    const deleteStore = () => {
        listStores.delete(storeId);
    };

    // just a function to call from a component to keep the store alive
    const keepAlive = () => ({});

    const items = computed(() => {
        const orderByField = unref(store.orderBy).field || store.key;
        const direction = unref(store.orderBy).dir === 'asc' ? 1 : -1;

        return Object.values(unref(store.items)).sort((a, b) => direction * (a[orderByField] - b[orderByField]));
    });
    const itemsMap = computed(() => unref(store.items));
    const item = (id) => store.items.value[id];
    const selectedItem = computed(() => store.items.value[unref(store.selectedItemId)] || null);

    onUnmounted(() => {
        setTimeout(() => cleanup(), options?.cacheTime || 0);
    });

    return {
        subscriberUuid,
        item,
        items,
        itemsMap,
        selectedItem,

        addItem,
        addItems,
        cleanup,
        clearList,
        deleteStore,
        keepAlive,
        removeItem,
        removeItems,
        selectItem,
        setOrderBy,
        updateItem,
    };
}
